import React from "react";
import parse from "html-react-parser";
import { Box, Flex, Text } from "@indeed/ifl-components";
import { Grid, GridItem } from "components/grid";
import { AnimationVideo } from "components/animationVideo";
import FeatureImage from "./featureImage";
import CopyButton from "./copyButton";
import { FacebookButton, LinkedInButton, TwitterButton } from "./socialButtons";
import { Ellipse } from "./ellipse";
import { ArticleHeroCreditStyles } from "styles/common";
import type { SystemStyleObject } from "@indeed/ifl-css";
import {
  formatDate,
  extractContributors,
  parseContributorTitles,
} from "helpers/utils";
import { getCategoryColor } from "helpers/getCategoryColor";
import parseArticleHeroContributors from "helpers/parseArticleHeroContributors";

const Categories: React.FC<{ items: { slug: string; name: string }[] }> = ({
  items,
}) => {
  return items && items.length ? (
    <Box as="div">
      {items.map(({ slug, name }) => (
        <Text
          key={slug}
          sx={{
            mr: 3,
            fontWeight: "bold",
            color: getCategoryColor(slug),
            lineHeight: "normal",
            fontSize: [2, null, 3, 3],
          }}
        >
          {name}
        </Text>
      ))}
    </Box>
  ) : null;
};

export const SocialsRow: React.FC<{
  author: string;
  title: string;
  url: string;
  sx: SystemStyleObject;
  mediaType?: "animation" | "image";
}> = ({ sx, author, title, url, mediaType }) => {
  const withAnimationStyle: SystemStyleObject = {
    position: [null, null, null, "relative"],
    top: [null, null, null, "11px"],
    pr: [null, null, null, "4px"],
  };

  return (
    <Flex
      sx={[
        {
          justifyContent: [
            "flex-start",
            "flex-start",
            "flex-start",
            "flex-end",
          ],
          "& a": {
            mr: 1,
          },
          ...sx,
        },
        mediaType === "animation" ? withAnimationStyle : {},
      ]}
    >
      <TwitterButton text={`${title} by ${author} ${url}`} />
      <FacebookButton url={`${url}`} quote={`${title} by ${author}`} />
      <LinkedInButton url={`${url}`} title={`${title} by ${author}`} />
      <CopyButton
        label={["", "", "", "Copy link to share"]}
        text={`${url}`}
        sx={{
          border: "none",
          color: "neutral.900",
        }}
      />
    </Flex>
  );
};

const ArticlePageHero = ({ article, contributorList, title, subTitle }) => {
  const contributorObject = extractContributors(contributorList);

  const {
    author,
    altText,
    animationUrl,
    articleUrl,
    categories,
    gatsbyImageData,
    image,
    mediaType,
    publishDate,
  } = article;

  return (
    <Grid templateColumns="repeat(8, 1fr)">
      {/* Hero Image */}
      <GridItem colSpan={8} colStart={[1, 1, 1, 2]}>
        <Box as="figure" sx={{ mb: [6, null, mediaType, null] }}>
          {mediaType === "animation" && animationUrl && (
            <AnimationVideo
              url={animationUrl}
              containerStyles={{
                marginLeft: ["-10vw", "-10vw", 0],
                maxWidth: "100vw",
                width: ["100vw", "100vw", "100%", "100%"],
              }}
              playButtonContainerStyles={{
                flexDirection: "row-reverse",
                backgroundColor: "white",
                pr: ["15px", null, 0],
                height: 0,
              }}
            />
          )}
          {mediaType === "image" && image && (
            <FeatureImage
              imageUrl={image}
              altText={altText}
              fluidImage={gatsbyImageData}
              sx={{
                mb: 3,
                borderRadius: 0,
                boxShadow: 0,
              }}
            />
          )}
        </Box>
        <SocialsRow
          author={author}
          title={title}
          url={articleUrl}
          mediaType={mediaType}
          sx={{
            display: ["none", null, null, "flex"],
          }}
        />
      </GridItem>
      {/* Title/subtitle */}
      <GridItem
        colSpan={[8, 8, 8, 6]}
        colStart={1}
        sx={{
          pb: [3, 3, 3, 7],
          mt: [0, 0, 0, ({ space }) => `calc(-${space[6]} + -${space[9]})`],
          zIndex: ["inherit", null, null, 0],
        }}
      >
        <Text
          as="h1"
          sx={{
            fontWeight: 700,
            lineHeight: ["normal", null, "tight", "tight"],
            fontSize: [6, null, 7, 7],
            color: "neutral.1000",
            textDecoration: "none",
            maxWidth: ["100%", null, null, "35ch"],
            bg: ["transparent", null, null, "white"],
            pt: [0, 0, 0, 6],
            pr: [0, 0, 0, 4],
            m: 0,
            mb: 2,
          }}
        >
          {title}
        </Text>
        {subTitle ? (
          <Text
            sx={{
              "> p": {
                maxWidth: ["100%", null, null, "45ch"],
                fontWeight: "normal",
                lineHeight: "normal",
                fontSize: [4, null, 6, 6],
                m: 0,
                mb: 4,
              },
            }}
          >
            {parse(subTitle)}
          </Text>
        ) : null}
        {/* Contributor Credit */}
        <Flex
          sx={{
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          {Object.entries(contributorObject).map(([key, contributorArr]) => {
            if (!contributorArr.length) {
              return null;
            }
            return (
              <Flex
                key={key}
                as="div"
                sx={{
                  alignItems: "baseline",
                  flexWrap: "wrap",
                }}
              >
                <Text
                  level="articleauthor"
                  sx={{ ...ArticleHeroCreditStyles, fontWeight: 700 }}
                >
                  {parseContributorTitles(key, contributorArr)}
                  &nbsp;
                </Text>
                {parseArticleHeroContributors(contributorArr)}
              </Flex>
            );
          })}
        </Flex>
        {/* Published date and categories */}
        <Flex
          sx={{
            flexDirection: ["column", null, "row", null],
            flexWrap: "wrap",
            mb: [3, 3, 3, 0],
            alignItems: ["start", null, "center"],
          }}
        >
          {publishDate ? (
            <Text
              sx={{
                lineHeight: "normal",
                fontSize: [2, null, 3, 3],
              }}
            >
              {formatDate(publishDate)}
            </Text>
          ) : null}
          {categories && categories.length ? (
            <Flex sx={{ alignItems: "center" }}>
              <Ellipse
                sx={{
                  color: "neutral.600",
                  mx: 3,
                  display: ["none", null, "inherit", "inherit"],
                }}
              />
              <Categories items={categories} />
            </Flex>
          ) : null}
        </Flex>
        <SocialsRow
          author={author}
          title={article.title}
          url={articleUrl}
          mediaType={mediaType}
          sx={{ display: ["flex", null, null, "none"] }}
        />
      </GridItem>
    </Grid>
  );
};

export default ArticlePageHero;
